import { Button } from '@cube3/cubicle/src/core/atoms/Button';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import React, {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useModalActions } from '../../Modals/modalActions';
import { useDispatch } from 'react-redux';
import { actionCreators } from '@cube3/state/src/redux/ducks/session';
import { useTypedSelector } from '@cube3/state/src/redux/components/Hooks/useTypedSelector';
import { HBox, VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import {
  clearSubmitErrors,
  getFormError,
  getFormSyncErrors,
  getFormValues,
  InjectedFormProps,
  reduxForm
} from 'redux-form';
import { compose } from '../../../../../utils/component-helpers';
import { getErrorMessage } from '../../../../forms/helpers/errors';
import { addFormId } from '@cube3/state/src/redux/middleware/redux-form-middleware';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import {
  RecoveryCodesToPrint,
  usePrintRecoveryCodesCSS
} from '@cube3/ui/src/Layout/AccountSettings/SubComponents/RecoveryCodesToPrint';
import { useReactToPrint } from 'react-to-print';
import { useGlobalSubmit } from '@cube3/cubicle/src/helpers/hooks/useGlobalSubmit';
import { GenericReduxForm } from '@cube3/forms/src/forms/GenericReduxForm';
import { Field } from '@cube3/forms/src/types/GenericFormTypes';
import { isMobile } from 'react-device-detect';

interface Props extends InjectedFormProps {}

export const ViewRecoveryCodesBase = (props: Props) => {
  const { form, submitting, submitFailed, invalid } = props;

  /** if should regenerate recovery codes */
  const [regen, setRegen] = useState(false);
  const classes = usePrintRecoveryCodesCSS();

  const { previousModal } = useModalActions();

  const formValues = useTypedSelector((state) => getFormValues(form)(state));
  const otpCode = formValues?.['totp_code'];
  const submitErrors = useTypedSelector((state) => getFormError(form)(state));
  const syncErrors = useTypedSelector((state) =>
    getFormSyncErrors(form)(state)
  );
  const errorMessage = submitErrors
    ? getErrorMessage(submitErrors[0])
    : syncErrors?.['totp_code'];

  const stored = useTypedSelector((state) => state.session);
  const recoveryCode = stored?.recoveryCode;

  const contentRef = useRef(null);
  const reactToPrintFn = useReactToPrint({ contentRef });
  const handlePrint = useCallback(() => {
    reactToPrintFn();
  }, [reactToPrintFn]);

  const handleCopy = useCallback(() => {
    if (!recoveryCode) return;
    navigator.clipboard.writeText(recoveryCode.join('\n'));
  }, [recoveryCode]);

  const clearRecoveryCodes = useCallback(() => {
    dispatch(actionCreators.clearEnrollCredentials());
  }, []);

  const dispatch = useDispatch();

  const handleGenerateCodes = useCallback(() => {
    if (!otpCode) return;
    dispatch(
      addFormId(form)(actionCreators.generateRecoveryCode(otpCode, regen))
    );
  }, [otpCode]);

  const onClickRegenerate = useCallback(() => {
    setRegen(true);
    clearRecoveryCodes();
  }, [clearRecoveryCodes]);

  const fields = useMemo(() => {
    return [
      {
        type: 'authentication',
        name: 'totp_code',
        autoFocus: true,
        label: 'Enter verificaion code:',
        shouldClear: !!submitErrors, // to clear the `AuthCode` input
        submitError: (submitFailed || invalid) && errorMessage
      } as Field<string, 'authentication'>
    ];
  }, [submitErrors, submitFailed, invalid, errorMessage]);

  // clear codes whenever the modal is mounted/unmounted
  useLayoutEffect(() => {
    clearRecoveryCodes();
    return () => clearRecoveryCodes();
  }, []);

  useGlobalSubmit(handleGenerateCodes, false);

  const disableVerify =
    !otpCode || submitting || invalid || !!submitErrors || otpCode.length !== 6;

  return (
    <ModalMenuUI
      title={'View recovery codes'}
      loading={submitting}
      onCloseEvent={previousModal}
      footerRightComponent={
        !recoveryCode ? (
          <Button
            label="Verify"
            background="primary"
            onClick={handleGenerateCodes}
            buttonStyle="solid"
            disabled={disableVerify}
          />
        ) : (
          <Button
            label="Close"
            onClick={previousModal}
            buttonStyle="outline"
            size={isMobile ? SizeVariants.lg : SizeVariants.md}
          />
        )
      }
    >
      {!recoveryCode && !submitting ? (
        <GenericReduxForm fields={fields} direction="vertical" />
      ) : (
        recoveryCode && (
          <VBox
            gap={isMobile ? 26 : 16}
            style={{
              marginTop: isMobile ? 16 : '',
              marginBottom: isMobile ? 20 : ''
            }}
            justify="start"
          >
            <Typography
              weight="body"
              colorVariant="03"
              variant={isMobile ? SizeVariants.lg : SizeVariants.md}
            >
              Recovery codes can be used to access your account in the event you
              lose access to your device and cannot receive two-factor
              authentication codes. Recovery codes should be used in order.
            </Typography>
            <HBox gap={16} justify="start">
              <Button
                label={isMobile ? 'Regenerate' : 'Regenerate codes'}
                buttonStyle="outline"
                iconLeft="refresh"
                onClick={onClickRegenerate}
                size={isMobile ? SizeVariants.lg : SizeVariants.sm}
              />
              <Button
                label="Copy"
                iconLeft="copy"
                onClick={handleCopy}
                buttonStyle="outline"
                size={isMobile ? SizeVariants.lg : SizeVariants.sm}
              />
              <Button
                label="Print"
                iconLeft="print"
                onClick={handlePrint}
                buttonStyle="outline"
                size={isMobile ? SizeVariants.lg : SizeVariants.sm}
              />
            </HBox>
            <div
              className={classes.codes}
              style={{ marginLeft: isMobile ? 10 : '' }}
            >
              {recoveryCode.map((code) => (
                <span key={code} className={classes.code}>
                  <Typography
                    weight="body"
                    colorVariant="02"
                    variant={isMobile ? SizeVariants.lg : SizeVariants.md}
                  >
                    {code}
                  </Typography>
                </span>
              ))}
            </div>
            <div style={{ display: 'none' }}>
              <RecoveryCodesToPrint
                contentRef={contentRef}
                codes={recoveryCode}
              />
            </div>
          </VBox>
        )
      )}
    </ModalMenuUI>
  );
};

export const ViewRecoveryCodesModal = compose(ViewRecoveryCodesBase)(
  reduxForm({
    form: 'view_recovery_codes',
    destroyOnUnmount: true,
    onChange: (values, dispatch, props) => {
      // clear submit errors when changing input after submit failed
      if (values?.['totp_code'] && props.submitFailed) {
        dispatch(clearSubmitErrors(props.form));
      }
    }
  })
);
